import * as Sentry from '@sentry/browser';
import { beforeSend } from './redact';

const isDev = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';
const sentryDsn = typeof process !== 'undefined' ? process.env.SENTRY_DSN : undefined;
const sentryEnv =
  typeof process !== 'undefined' && process.env.NODE_ENV ? process.env.NODE_ENV : 'production';

if (!isDev && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnv,
    beforeSend,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

export const log = {
  debug: (message: string, context?: Record<string, unknown>) => {
    if (isDev) {
      console.debug(message, context);
    } else {
      Sentry.addBreadcrumb({
        message,
        level: 'debug',
        ...(context && { data: context }),
      });
    }
  },

  info: (message: string, context?: Record<string, unknown>) => {
    if (isDev) {
      console.info(message, context);
    } else {
      Sentry.addBreadcrumb({
        message,
        level: 'info',
        ...(context && { data: context }),
      });
    }
  },

  warn: (message: string, context?: Record<string, unknown>) => {
    if (isDev) {
      console.warn(message, context);
    } else {
      Sentry.captureMessage(message, {
        level: 'warning',
        contexts: { extra: context },
      });
    }
  },

  error: (message: string, error?: Error, context?: Record<string, unknown>) => {
    if (isDev) {
      console.error(message, error, context);
    } else {
      if (error) {
        Sentry.captureException(error, {
          contexts: {
            extra: {
              message,
              ...context,
            },
          },
        });
      } else {
        Sentry.captureMessage(message, {
          level: 'error',
          contexts: { extra: context },
        });
      }
    }
  },
} as const;

// `logger` is the canonical name across the codebase; `log` is kept as
// an alias for older imports. Both refer to the same impl.
export const logger = log;
